import React from 'react';
import { Clock, CheckCircle2, Ban, AlertTriangle, UserPlus, Copy, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';

const BADGE = {
  pendiente: { icono: Clock, texto: 'Pendiente', clase: 'bg-amber-100 text-amber-800' },
  firmado: { icono: CheckCircle2, texto: 'Firmado', clase: 'bg-green-100 text-green-800' },
  caducada: { icono: AlertTriangle, texto: 'Caducada', clase: 'bg-red-100 text-red-700' },
  anulado: { icono: Ban, texto: 'Anulada', clase: 'bg-gray-100 text-gray-600' },
};

const urlDe = (s) => s.url || (s.token ? `${window.location.origin}/firmar/${s.token}` : '');

const copiar = async (texto) => {
  try {
    await navigator.clipboard.writeText(texto);
    toast({ title: 'Enlace copiado' });
  } catch {
    toast({ title: 'Copia manualmente el enlace', description: texto });
  }
};

const agrupar = (solicitudes) => {
  const grupos = new Map();
  solicitudes.forEach((s) => {
    const clave = s.grupo || `solo-${s.id}`;
    if (!grupos.has(clave)) grupos.set(clave, []);
    grupos.get(clave).push(s);
  });
  return [...grupos.entries()].map(([clave, lista]) => {
    const activas = lista.filter((s) => s.estado !== 'anulado');
    return {
      clave,
      lista: [...lista].sort((a, b) => (a.grupo_orden || a.id) - (b.grupo_orden || b.id)),
      conjunta: activas.length > 1,
      total: activas.length,
      firmadas: activas.filter((s) => s.estado === 'firmado').length,
      pendientes: activas.filter((s) => s.estado === 'pendiente').length,
      base: activas[0] || lista[0],
    };
  });
};

// Solicitudes de firma de un documento, agrupadas por firma conjunta.
const GrupoSolicitudes = ({ solicitudes, onGestionar, onAnadirFirmante }) => {
  if (!solicitudes?.length) return null;
  const grupos = agrupar(solicitudes);

  return (
    <div className="space-y-2" data-testid="firma-solicitudes-list">
      <div className="text-xs text-gray-500">Solicitudes de este documento</div>
      {grupos.map((g) => (
        <div key={g.clave} className="rounded-md border border-gray-200 bg-gray-50 p-2 space-y-1.5"
             data-testid={`firma-grupo-${g.clave}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
              {g.conjunta && <Users className="w-3.5 h-3.5 text-indigo-600" />}
              {g.conjunta
                ? `Firma conjunta · ${g.firmadas} de ${g.total} firmadas`
                : 'Petición individual'}
            </span>
            {g.pendientes > 0 && (
              <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]"
                      onClick={() => onAnadirFirmante(g)}
                      data-testid={`firma-anadir-firmante-btn-${g.clave}`}>
                <UserPlus className="w-3 h-3 mr-1 text-indigo-600" /> Añadir firmante
              </Button>
            )}
          </div>

          {g.conjunta && g.firmadas > 0 && g.pendientes > 0 && (
            <div className="text-[11px] text-amber-800" data-testid={`firma-grupo-parcial-${g.clave}`}>
              Aún faltan {g.pendientes} firmante(s): la actuación y las copias se generan al firmar el último.
            </div>
          )}

          {g.lista.map((s) => {
            const badge = BADGE[s.estado] || BADGE.anulado;
            const Icono = badge.icono;
            const url = urlDe(s);
            return (
              <div key={s.id} className="flex items-center justify-between gap-2 text-xs bg-white rounded px-2 py-1"
                   data-testid={`firma-solicitud-${s.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 flex-shrink-0 ${badge.clase}`}>
                    <Icono className="w-3 h-3" /> {badge.texto}
                  </span>
                  <span className="truncate text-gray-700">
                    {s.firmante_nombre || 'firmante'}
                    <span className="text-gray-400">
                      {' · '}
                      {s.presencial ? 'en el despacho' : (s.email_enviado_a || 'enlace manual')}
                      {s.firmado_at ? ` · ${new Date(s.firmado_at).toLocaleString('es-ES')}` : ''}
                    </span>
                  </span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {s.estado === 'pendiente' && url && (
                    <Button size="sm" variant="ghost" className="h-6 px-1.5" onClick={() => copiar(url)}
                            title="Copiar el enlace de firma"
                            data-testid={`firma-copiar-enlace-${s.id}`}>
                      <Copy className="w-3 h-3 text-gray-500" />
                    </Button>
                  )}
                  {s.estado === 'pendiente' && (
                    <Button size="sm" variant="ghost" className="h-6 px-1.5" onClick={() => onGestionar(s)}
                            title="Cancelar o eliminar"
                            data-testid={`firma-anular-btn-${s.id}`}>
                      <Ban className="w-3 h-3 text-red-600" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default GrupoSolicitudes;
